import React, { useState, useEffect } from 'react';
import maintenanceOrderService from '../api/maintenanceOrderService';
import projectService from '../api/projectService';
import userService from '../api/userService';
import Modal from '../components/Modal';
import MaintenanceOrderForm from '../components/MaintenanceOrderForm';
import Button from '../components/Button';
import RowActions from '../components/RowActions';
import { Ban, Eye, Pencil } from 'lucide-react';
// Escala semántica de estado — fuente única de verdad (ver src/theme/status.js)
import { ORDER_STATUS_TONE, getStatusBadgeStyle as getBadgeStyleForTone } from '../theme/status';
// Estados de carga/error compartidos (ver hallazgo de auditoría #08)
import LoadingState from '../components/LoadingState';
import ErrorState from '../components/ErrorState';

// Traducciones para tipo de mantenimiento
const MAINTENANCE_TYPE_LABELS = {
  PREVENTIVE: 'Preventivo',
  CORRECTIVE: 'Correctivo',
};

// Traducciones para el estado de la orden de mantenimiento
const STATUS_LABELS = {
  PENDING: 'Pendiente',
  ASSIGNED: 'Asignada',
  IN_PROGRESS: 'En progreso',
  COMPLETED: 'Completada',
  CANCELED: 'Cancelada',
};

/**
 * @description Formatea una fecha ISO al formato dd/mm/aaaa.
 * @param {string | null} dateString Fecha en formato ISO.
 * @returns {string} Fecha formateada o '—' si no existe.
 */
const formatDate = (dateString) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

/**
 * @description Calcula el porcentaje de cumplimiento de un reporte de mantenimiento.
 * Se basa en 17 ítems fijos como denominador (consistente con MaintenanceReports.js).
 * @param {Array<boolean>} checklistResults Array de booleanos con los resultados del checklist.
 * @returns {number} Porcentaje redondeado de 0 a 100.
 */
const calcularCumplimiento = (checklistResults) => {
  if (!Array.isArray(checklistResults) || checklistResults.length === 0) return 0;
  const passed = checklistResults.filter(Boolean).length;
  return Math.round((passed / 17) * 100);
};

/**
 * @description Genera el estilo del badge de estado de la orden de mantenimiento.
 * Delega en la escala semántica compartida (theme/status.js) — mismo enum de
 * estado que WorkOrders.js y Dashboard.js, ahora con un único mapeo real.
 * @param {string} status Valor del estado (PENDING, ASSIGNED, COMPLETED, CANCELED).
 * @returns {object} Objeto de estilos inline.
 */
const getStatusBadgeStyle = (status) => getBadgeStyleForTone(ORDER_STATUS_TONE[status]);

/**
 * @description Detecta si una orden fue reportada por el técnico con el checklist
 * incompleto (regla agregada en la app móvil: el técnico puede justificar en
 * `general_notes` y enviar el reporte sin completar el 100% del checklist; en ese
 * caso el backend NO cierra la orden, mantiene su status previo). El status de
 * backend no cambia — no hay un estado nuevo — pero el panel debe mostrar una
 * etiqueta distinta para que el administrador note que algo quedó pendiente.
 * @param {object} order Orden con su `report` incluido (lista o detalle).
 * @returns {boolean}
 */
const isIncompleteReport = (order) => {
  if (!order.report || order.status === 'COMPLETED' || order.status === 'CANCELED') {
    return false;
  }
  const results = order.report.checklist_results;
  return Array.isArray(results) && results.some((v) => v !== true);
};

/**
 * @description Resuelve la etiqueta y el estilo de badge a mostrar para el estado
 * de una orden, sobreescribiendo con "No Completada" (tono 'alert') cuando aplica
 * la regla de checklist incompleto — ver `isIncompleteReport`.
 * @param {object} order
 * @returns {{ label: string, style: object }}
 */
const getDisplayStatus = (order) => {
  if (isIncompleteReport(order)) {
    return { label: 'No Completada', style: getBadgeStyleForTone('alert') };
  }
  return { label: STATUS_LABELS[order.status] || order.status, style: getStatusBadgeStyle(order.status) };
};

/**
 * @description Genera el estilo del badge de tipo de mantenimiento.
 * Consistente con el badge de MaintenanceReports.js.
 * @param {string} type Valor del tipo (PREVENTIVE, CORRECTIVE).
 * @returns {object} Objeto de estilos inline.
 */
const getTypeBadgeStyle = (type) => {
  const colors = {
    PREVENTIVE: { backgroundColor: '#f0f9f0', color: '#2e7d32', border: '1px solid #a5d6a7' },
    CORRECTIVE: { backgroundColor: '#fff3e0', color: '#e65100', border: '1px solid #ffcc80' },
  };
  return {
    padding: '2px 8px',
    borderRadius: '12px',
    fontSize: '0.8rem',
    fontWeight: 'bold',
    ...(colors[type] || { backgroundColor: '#f0f0f0', color: '#555' }),
  };
};

/**
 * @description Página de gestión de Órdenes de Mantenimiento.
 * Permite visualizar todas las órdenes, crear nuevas, ver su detalle y cancelarlas.
 * Disponible para roles ADMIN y SUPERVISOR.
 * @returns {JSX.Element}
 */
const MaintenanceOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  // Error de carga inicial — bloquea el render de la tabla
  const [loadError, setLoadError] = useState('');

  // Paginación
  const LIMIT = 20;
  const [meta, setMeta] = useState({ total: 0, page: 1, limit: 20, totalPages: 1 });
  // Error de operación (crear, cancelar) — se muestra encima de la tabla sin bloquear
  const [actionError, setActionError] = useState('');

  // Estado del modal de nueva orden / edición — mismo modal para ambos modos,
  // igual que WorkOrders.js. currentOrder null = creación, objeto = edición.
  const [showFormModal, setShowFormModal] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);
  // Carga lazy de proyectos y técnicos — solo al primer abrir el modal
  const [projectList, setProjectList] = useState([]);
  const [technicianList, setTechnicianList] = useState([]);
  const [dependenciesLoading, setDependenciesLoading] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);
  // Carga del detalle completo de la orden a editar (project_id/equipment_id/notes reales,
  // no solo lo que trae la fila de la tabla)
  const [editLoading, setEditLoading] = useState(false);

  // ID de la orden pendiente de confirmar cancelación (null = modal cerrado)
  const [confirmCancelId, setConfirmCancelId] = useState(null);

  // Estado del modal de detalle de una orden
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailOrder, setDetailOrder] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');

  // Carga las órdenes al montar el componente
  useEffect(() => {
    fetchOrders();
  }, []);

  /**
   * @description Carga la lista completa de órdenes de mantenimiento desde la API.
   */
  const fetchOrders = async (targetPage = 1) => {
    setLoadError('');
    try {
      const response = await maintenanceOrderService.getAllMaintenanceOrders({ page: targetPage, limit: LIMIT });
      setOrders(response.data.data);
      setMeta(response.data.meta);
    } catch (error) {
      console.error('Error al cargar órdenes de mantenimiento:', error);
      setLoadError('No se pudo cargar las órdenes de mantenimiento. Intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * @description Muestra un error de operación y lo limpia automáticamente tras 6 segundos.
   * @param {string} message Mensaje de error a mostrar.
   */
  const showActionError = (message) => {
    setActionError(message);
    setTimeout(() => setActionError(''), 6000);
  };

  /**
   * @description Carga proyectos y técnicos si aún no se cargaron (lazy, cacheada en estado
   * para evitar peticiones duplicadas). Compartida por el modal de creación y de edición.
   */
  const loadFormDependencies = async () => {
    if (projectList.length > 0 && technicianList.length > 0) return;

    setDependenciesLoading(true);
    try {
      const [projectsRes, techniciansRes] = await Promise.all([
        projectList.length === 0
          ? projectService.getProjectsLookup()
          : Promise.resolve({ data: { data: projectList } }),
        technicianList.length === 0
          ? userService.getTechniciansLookup()
          : Promise.resolve({ data: { data: technicianList } }),
      ]);
      setProjectList(projectsRes.data.data);
      setTechnicianList(techniciansRes.data.data);
    } catch (error) {
      console.error('Error al cargar dependencias del formulario:', error);
      // No bloqueamos el formulario: los selectores quedarán vacíos con mensaje al usuario
    } finally {
      setDependenciesLoading(false);
    }
  };

  /**
   * @description Abre el modal en modo creación (currentOrder = null).
   */
  const openFormModal = async () => {
    setCurrentOrder(null);
    setShowFormModal(true);
    await loadFormDependencies();
  };

  /**
   * @description Abre el modal en modo edición/asignación. Primero obtiene el detalle
   * completo de la orden (GET /maintenance-orders/:id) porque la fila de la tabla no trae
   * los IDs crudos (project_id, equipment_id, assigned_to) ni las notas — solo los objetos
   * anidados para mostrar. Mismo patrón que openDetailModal, pero alimenta el formulario.
   * @param {object} order La orden de la fila seleccionada.
   */
  const openEditModal = async (order) => {
    setShowFormModal(true);
    setEditLoading(true);
    try {
      const response = await maintenanceOrderService.getMaintenanceOrderById(order.order_id);
      setCurrentOrder(response.data);
    } catch (error) {
      console.error('Error al cargar la orden para editar:', error);
      showActionError('No se pudo cargar la orden para editar. Intente nuevamente.');
      setShowFormModal(false);
      setEditLoading(false);
      return;
    }
    setEditLoading(false);
    await loadFormDependencies();
  };

  /**
   * @description Cierra el modal del formulario (creación o edición) y limpia el estado asociado.
   */
  const closeFormModal = () => {
    setShowFormModal(false);
    setCurrentOrder(null);
  };

  /**
   * @description Maneja el envío del formulario de orden de mantenimiento, tanto en creación
   * como en edición/reasignación. Llama al servicio correspondiente, cierra el modal y recarga
   * la lista (página 1 al crear para mostrar la nueva orden; página actual al editar).
   * @param {object} data Payload del formulario listo para enviar al backend.
   */
  const handleFormSubmit = async (data) => {
    setFormSubmitting(true);
    try {
      if (currentOrder) {
        await maintenanceOrderService.updateMaintenanceOrder(currentOrder.order_id, data);
      } else {
        await maintenanceOrderService.createMaintenanceOrder(data);
      }
      closeFormModal();
      const targetPage = currentOrder ? meta.page : 1;
      const response = await maintenanceOrderService.getAllMaintenanceOrders({ page: targetPage, limit: LIMIT });
      setOrders(response.data.data);
      setMeta(response.data.meta);
    } catch (error) {
      console.error('Error al guardar orden de mantenimiento:', error);
      showActionError(
        error.response?.data?.message ||
          `No se pudo ${currentOrder ? 'actualizar' : 'crear'} la orden de mantenimiento.`
      );
    } finally {
      setFormSubmitting(false);
    }
  };

  /**
   * @description Abre el modal de detalle de una orden.
   * Solicita el detalle completo al backend para obtener datos relacionados (reporte, etc.).
   * @param {object} order La orden de la fila seleccionada.
   */
  const openDetailModal = async (order) => {
    setDetailOrder(null);
    setDetailError('');
    setDetailLoading(true);
    setShowDetailModal(true);

    try {
      const response = await maintenanceOrderService.getMaintenanceOrderById(order.order_id);
      setDetailOrder(response.data);
    } catch (error) {
      console.error('Error al cargar detalle de la orden:', error);
      setDetailError('No se pudo cargar el detalle de la orden. Intente nuevamente.');
    } finally {
      setDetailLoading(false);
    }
  };

  /**
   * @description Cierra el modal de detalle y limpia el estado asociado.
   */
  const closeDetailModal = () => {
    setShowDetailModal(false);
    setDetailOrder(null);
    setDetailError('');
  };

  /**
   * @description Cancela una orden de mantenimiento previa confirmación del usuario.
   * Recarga la lista al completar la operación.
   * @param {number | string} orderId ID de la orden a cancelar.
   */
  const handleCancel = (orderId) => {
    setConfirmCancelId(orderId);
  };

  const executeCancel = async () => {
    const orderId = confirmCancelId;
    setConfirmCancelId(null);
    try {
      await maintenanceOrderService.cancelMaintenanceOrder(orderId);
      const response = await maintenanceOrderService.getAllMaintenanceOrders({ page: meta.page, limit: LIMIT });
      setOrders(response.data.data);
      setMeta(response.data.meta);
    } catch (error) {
      console.error('Error al cancelar orden de mantenimiento:', error);
      showActionError(
        error.response?.data?.message || 'No se pudo cancelar la orden de mantenimiento.'
      );
    }
  };

  if (loading) return <LoadingState message="Cargando órdenes de mantenimiento..." />;

  // Error de carga inicial: no tiene sentido mostrar la tabla sin datos
  if (loadError) return <ErrorState message={loadError} />;

  return (
    <div>
      <h2>Órdenes de Mantenimiento</h2>

      {/* Alerta de error de operación: visible sobre la tabla, se cierra manualmente o tras 6s */}
      {actionError && (
        <div style={styles.actionErrorBanner}>
          <span>{actionError}</span>
          <button
            onClick={() => setActionError('')}
            style={styles.actionErrorClose}
            aria-label="Cerrar mensaje de error"
          >
            &times;
          </button>
        </div>
      )}

      {/* Barra de acciones: botón para crear nueva orden */}
      <div style={styles.toolbar}>
        {/* Acción primaria de la página: relleno sólido en teal de marca */}
        <Button variant="primary" onClick={openFormModal} style={{ marginLeft: 'auto' }}>
          Nueva Orden
        </Button>
      </div>

      {/* Contador de órdenes cargadas */}
      <p style={styles.resultCount}>
        {orders.length}{' '}
        {orders.length === 1 ? 'orden encontrada' : 'órdenes encontradas'}
      </p>

      {/* Estado de lista vacía */}
      {orders.length === 0 ? (
        <p style={styles.emptyMessage}>No hay órdenes de mantenimiento registradas.</p>
      ) : (
        <div className="table-container">
        <table style={styles.table}>
          <thead>
            <tr style={styles.headerRow}>
              <th style={styles.th} className="col-hide-mobile">#</th>
              <th style={styles.th}>Proyecto</th>
              <th style={styles.th} className="col-hide-mobile">Equipo</th>
              <th style={styles.th}>Tipo</th>
              <th style={styles.th} className="col-hide-mobile">Técnico</th>
              <th style={styles.th}>Estado</th>
              <th style={styles.th} className="col-hide-mobile">Programado</th>
              <th style={styles.th} className="col-hide-mobile">Reporte</th>
              <th style={styles.th} className="col-actions-sticky">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((order, index) => (
              <tr
                key={order.order_id}
                style={{ ...styles.row, cursor: 'pointer' }}
                onClick={() => openDetailModal(order)}
              >
                <td style={{ ...styles.td, color: 'rgba(56,62,65,0.45)', fontSize: '0.85rem' }} className="col-hide-mobile">
                  {index + 1}
                </td>
                <td style={styles.td}>{order.project?.name || '—'}</td>
                <td style={styles.td} className="col-hide-mobile">{order.equipment?.name || '—'}</td>
                <td style={styles.td}>
                  <span style={getTypeBadgeStyle(order.maintenance_type)}>
                    {MAINTENANCE_TYPE_LABELS[order.maintenance_type] || order.maintenance_type}
                  </span>
                </td>
                <td style={styles.td} className="col-hide-mobile">{order.assignee?.username || '—'}</td>
                <td style={styles.td}>
                  <span style={getDisplayStatus(order).style}>
                    {getDisplayStatus(order).label}
                  </span>
                </td>
                <td style={{ ...styles.td, whiteSpace: 'nowrap' }} className="col-hide-mobile">
                  {formatDate(order.scheduled_for)}
                </td>
                <td style={styles.td} className="col-hide-mobile">
                  {order.report ? (
                    <span style={styles.reportAvailable}>Ver reporte</span>
                  ) : (
                    <span style={styles.reportPending}>Pendiente</span>
                  )}
                </td>
                <td style={styles.td} className="col-actions-sticky">
                  {/* "Ver detalle" siempre visible (misma acción que el click de
                      fila, pero explícita — evita que la columna se vea vacía
                      en órdenes COMPLETED/CANCELED). Cancelar reusa el mismo
                      lenguaje visual de "excepción destructiva" que Eliminar
                      (solo-ícono, rojo al hover), pero con el ícono Ban
                      (prohibido) en vez de Trash2: no es un borrado, es dejar
                      la orden sin efecto. Solo se ofrece si la orden sigue
                      activa. Ninguna acción propaga el click a la fila. */}
                  <RowActions
                    actions={[
                      {
                        key: 'detail',
                        label: 'Ver detalle',
                        icon: Eye,
                        onClick: (e) => {
                          e.stopPropagation();
                          openDetailModal(order);
                        },
                      },
                      {
                        key: 'edit',
                        label: 'Editar / Asignar',
                        icon: Pencil,
                        onClick: (e) => {
                          e.stopPropagation();
                          openEditModal(order);
                        },
                      },
                    ]}
                    onDelete={
                      order.status !== 'CANCELED' && order.status !== 'COMPLETED'
                        ? (e) => {
                            e.stopPropagation();
                            handleCancel(order.order_id);
                          }
                        : undefined
                    }
                    deleteLabel="Cancelar"
                    deleteIcon={Ban}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}

      {/* Controles de paginación */}
      {meta.totalPages > 1 && (
        <div style={styles.pagination}>
          <button
            onClick={() => fetchOrders(meta.page - 1)}
            disabled={meta.page <= 1}
            style={{ ...styles.pageButton, opacity: meta.page <= 1 ? 0.4 : 1 }}
          >
            ← Anterior
          </button>
          <span style={styles.pageInfo}>
            Página {meta.page} de {meta.totalPages} &middot; {meta.total} orden{meta.total !== 1 ? 'es' : ''}
          </span>
          <button
            onClick={() => fetchOrders(meta.page + 1)}
            disabled={meta.page >= meta.totalPages}
            style={{ ...styles.pageButton, opacity: meta.page >= meta.totalPages ? 0.4 : 1 }}
          >
            Siguiente →
          </button>
        </div>
      )}

      {/* Modal de nueva orden / edición de orden de mantenimiento */}
      <Modal
        show={showFormModal}
        onClose={closeFormModal}
        title={currentOrder ? 'Editar Orden de Mantenimiento' : 'Nueva Orden de Mantenimiento'}
        modalStyle={detailStyles.modalOverride}
      >
        {editLoading || dependenciesLoading ? (
          <LoadingState
            message={editLoading ? 'Cargando orden...' : 'Cargando datos del formulario...'}
            compact
          />
        ) : (
          <MaintenanceOrderForm
            order={currentOrder}
            projectList={projectList}
            technicianList={technicianList}
            onSubmit={handleFormSubmit}
            onCancel={closeFormModal}
            loading={formSubmitting}
          />
        )}
      </Modal>

      {/* Modal de detalle de una orden */}
      <Modal
        show={showDetailModal}
        onClose={closeDetailModal}
        title="Detalle de Orden de Mantenimiento"
        modalStyle={detailStyles.modalOverride}
      >
        {detailLoading && (
          <LoadingState message="Cargando detalle..." compact />
        )}

        {detailError && (
          <p style={detailStyles.errorText}>{detailError}</p>
        )}

        {!detailLoading && !detailError && detailOrder && (
          <OrderDetail order={detailOrder} />
        )}
      </Modal>

      {/* Modal de confirmación de cancelación */}
      <Modal
        show={confirmCancelId !== null}
        onClose={() => setConfirmCancelId(null)}
        title="Cancelar orden"
      >
        <p style={{ margin: 0, color: '#444' }}>
          ¿Está seguro de que desea cancelar esta orden de mantenimiento? Esta acción no se puede deshacer.
        </p>
        <div className="modal-footer">
          <button
            onClick={() => setConfirmCancelId(null)}
            className="modal-btn-secondary"
          >
            No, mantener
          </button>
          <button
            onClick={executeCancel}
            className="modal-btn-danger"
          >
            Sí, cancelar
          </button>
        </div>
      </Modal>
    </div>
  );
};

/**
 * @description Componente interno que renderiza el contenido del modal de detalle de una orden.
 * Muestra los datos generales y el resumen del reporte asociado si existe.
 * @param {object} props
 * @param {object} props.order Datos completos de la orden obtenidos de GET /maintenance-orders/:id.
 * @returns {JSX.Element}
 */
const OrderDetail = ({ order }) => {
  return (
    <div>
      {/* Sección: Información general de la orden */}
      <section style={detailStyles.section}>
        <h3 style={detailStyles.sectionTitle}>Información General</h3>
        <div style={detailStyles.infoGrid}>
          <div style={detailStyles.infoRow}>
            <span style={detailStyles.infoLabel}>Proyecto</span>
            <span style={detailStyles.infoValue}>{order.project?.name || '—'}</span>
          </div>
          <div style={detailStyles.infoRow}>
            <span style={detailStyles.infoLabel}>Equipo</span>
            <span style={detailStyles.infoValue}>{order.equipment?.name || '—'}</span>
          </div>
          <div style={detailStyles.infoRow}>
            <span style={detailStyles.infoLabel}>Tipo</span>
            <span style={detailStyles.infoValue}>
              <span style={getTypeBadgeStyle(order.maintenance_type)}>
                {MAINTENANCE_TYPE_LABELS[order.maintenance_type] || order.maintenance_type}
              </span>
            </span>
          </div>
          <div style={detailStyles.infoRow}>
            <span style={detailStyles.infoLabel}>Técnico</span>
            <span style={detailStyles.infoValue}>{order.assignee?.username || '—'}</span>
          </div>
          <div style={detailStyles.infoRow}>
            <span style={detailStyles.infoLabel}>Estado</span>
            <span style={detailStyles.infoValue}>
              <span style={getDisplayStatus(order).style}>
                {getDisplayStatus(order).label}
              </span>
            </span>
          </div>
          <div style={detailStyles.infoRow}>
            <span style={detailStyles.infoLabel}>Fecha programada</span>
            <span style={detailStyles.infoValue}>{formatDate(order.scheduled_for)}</span>
          </div>
          {order.notes && (
            <div style={detailStyles.infoRow}>
              <span style={detailStyles.infoLabel}>Notas</span>
              <span style={{ ...detailStyles.infoValue, whiteSpace: 'pre-wrap' }}>
                {order.notes}
              </span>
            </div>
          )}
        </div>
      </section>

      <hr style={detailStyles.divider} />

      {/* Sección: Reporte de mantenimiento asociado */}
      <section style={detailStyles.section}>
        <h3 style={detailStyles.sectionTitle}>Reporte de Mantenimiento</h3>

        {order.report ? (
          <ReportSummary report={order.report} />
        ) : (
          <p style={detailStyles.emptyText}>Sin reporte aún.</p>
        )}
      </section>
    </div>
  );
};

/**
 * @description Subcomponente que muestra un resumen del reporte asociado a la orden.
 * Muestra tipo, porcentaje de cumplimiento y fecha del reporte.
 * @param {object} props
 * @param {object} props.report Datos del reporte de mantenimiento.
 * @returns {JSX.Element}
 */
const ReportSummary = ({ report }) => {
  const pct = calcularCumplimiento(report.checklist_results);

  // Color del porcentaje: verde ≥80%, naranja 50-79%, rojo <50%
  const pctColor = pct >= 80 ? '#16a34a' : pct >= 50 ? '#d97706' : '#dc2626';

  return (
    <div style={detailStyles.infoGrid}>
      <div style={detailStyles.infoRow}>
        <span style={detailStyles.infoLabel}>Tipo de reporte</span>
        <span style={detailStyles.infoValue}>
          <span style={getTypeBadgeStyle(report.maintenance_type)}>
            {MAINTENANCE_TYPE_LABELS[report.maintenance_type] || report.maintenance_type}
          </span>
        </span>
      </div>
      <div style={detailStyles.infoRow}>
        <span style={detailStyles.infoLabel}>% Cumplimiento</span>
        <span style={{ ...detailStyles.infoValue, fontWeight: '600', color: pctColor }}>
          {pct}%
        </span>
      </div>
      <div style={detailStyles.infoRow}>
        <span style={detailStyles.infoLabel}>Fecha del reporte</span>
        <span style={detailStyles.infoValue}>{formatDate(report.created_at)}</span>
      </div>
      {report.general_notes && (
        <div style={detailStyles.infoRow}>
          <span style={detailStyles.infoLabel}>Observaciones</span>
          <span style={{ ...detailStyles.infoValue, whiteSpace: 'pre-wrap' }}>
            {report.general_notes}
          </span>
        </div>
      )}
    </div>
  );
};

// Estilos del modal de detalle — consistentes con MaintenanceReports.js
const detailStyles = {
  // Sobreescribe el width: 500px del componente Modal base via prop modalStyle
  modalOverride: {
    width: '700px',
  },
  // loading ahora vive en LoadingState (compact), ver hallazgo de auditoría #08
  errorText: {
    color: '#c0392b',
    textAlign: 'center',
    padding: '20px 0',
  },
  section: {
    marginBottom: '8px',
  },
  sectionTitle: {
    fontSize: '0.95rem',
    fontWeight: 'bold',
    color: '#444',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    marginBottom: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  divider: {
    border: 'none',
    borderTop: '1px solid #eee',
    margin: '16px 0',
  },
  infoGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  infoRow: {
    display: 'flex',
    gap: '12px',
    alignItems: 'baseline',
  },
  infoLabel: {
    minWidth: '150px',
    fontSize: '0.85rem',
    color: '#888',
    flexShrink: 0,
  },
  infoValue: {
    fontSize: '0.9rem',
    color: '#333',
  },
  emptyText: {
    color: '#999',
    fontStyle: 'italic',
    fontSize: '0.88rem',
    padding: '8px 0',
  },
};

// Estilos de la página — consistentes con MaintenanceReports.js y WorkOrders.js
const styles = {
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '12px',
    flexWrap: 'wrap',
  },
  resultCount: {
    fontSize: '0.85rem',
    color: '#666',
    marginBottom: '8px',
  },
  // Densidad, zebra, hover y columna de acciones fija ahora viven en
  // responsive.css (.table-container table ...) — ver hallazgo de auditoría #04.
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  headerRow: {},
  th: {},
  row: {},
  td: {},
  emptyMessage: {
    color: '#777',
    fontStyle: 'italic',
  },
  // Indicador verde cuando el reporte ya existe
  reportAvailable: {
    color: '#16a34a',
    fontSize: '0.85rem',
    fontWeight: '500',
  },
  // Indicador gris cuando la orden aún no tiene reporte
  reportPending: {
    color: '#9ca3af',
    fontSize: '0.85rem',
    fontStyle: 'italic',
  },
  // Banner de error para operaciones fallidas (crear, cancelar)
  actionErrorBanner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#fdecea',
    color: '#c0392b',
    border: '1px solid #f5c6cb',
    borderRadius: '4px',
    padding: '10px 14px',
    marginBottom: '14px',
    fontSize: '0.9rem',
  },
  actionErrorClose: {
    background: 'none',
    border: 'none',
    color: '#c0392b',
    cursor: 'pointer',
    fontSize: '1.2rem',
    lineHeight: '1',
    padding: '0 0 0 12px',
    fontWeight: 'bold',
  },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    padding: '16px 0',
    marginTop: '8px',
  },
  pageButton: {
    padding: '6px 16px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    background: '#fff',
    cursor: 'pointer',
    fontSize: '0.875rem',
    color: '#374151',
  },
  pageInfo: {
    fontSize: '0.875rem',
    color: '#6b7280',
  },
};

export default MaintenanceOrders;
